import { useCallback, useState } from 'react';
import { SelectValueType } from 'ui/selects';
import { BaseOption } from 'ui/selectv2/types';
import { getMultiSelectVariables, getSelectHandler } from './utils';
import { ConditionMultiValueProps } from '../types';
import { doesExist } from 'utils/tsHelpers';

export type Config<T> = Pick<
  ConditionMultiValueProps<T>,
  | 'value'
  | 'options'
  | 'onChange'
  | 'valuesLimit'
  | 'placeholder'
  | 'field'
  | 'onSelectChange'
  | 'isOpenByDefault'
>;

export const useMultiSelectState = <TValue extends SelectValueType>({
  value,
  onChange,
  onSelectChange,
  options = [],
  isOpenByDefault = false,
}: Config<TValue>) => {
  const { selectedValues, baseOptions, selectedBaseOptions } = getMultiSelectVariables(
    value ?? [],
    options,
  );

  /** state */
  const [isEditing, setIsEditing] = useState<boolean>(isOpenByDefault);
  const [inputValues, setInputValues] = useState<BaseOption<TValue>[]>(selectedBaseOptions);

  /** changeHandlers */
  const turnOnEditing = () => setIsEditing(true);
  const onSelect = getSelectHandler(value ?? [], setInputValues, onSelectChange);
  const onMultiSelectUpdate = useCallback(() => {
    // Don't trigger change if setting empty value for already empty value
    if (inputValues.length || value?.length) {
      onChange(inputValues.map(({ value: inputValue }) => inputValue));
    }
    setIsEditing(false);
  }, [inputValues, onChange, value]);

  /** variables */
  const hasEmptyValue = !doesExist(value) || value.length === 0;

  return {
    isEditing,
    hasEmptyValue,
    inputValues,
    selectedValues,
    baseOptions,
    turnOnEditing,
    onSelect,
    onMultiSelectUpdate,
  };
};
